import React from 'react';
import { Card, Kind, Greeting, Need, NeedsCard, HomeFrame, today, dueState } from './parts';
import ReleaseNote from '../../components/ReleaseNote';

/*
  Buyer Home.

  Built 5 September 2026, on the owner's decision. `buyer` had been a real job
  level in the database since 21 August and nobody had ever written down what one
  is for, so a buyer signing in got the REP's app: somebody whose job is ordering
  stock would have been shown screens asking them to count it. Put to him in
  plain words: **a buyer is somebody who orders stock.**

  **This Home is deliberately not like the other three, and the reason matters.**
  The other three count things, because counting leaves rows in the database to
  count. **The three ordering tools store nothing at all**: a spreadsheet goes in,
  a file comes out, and a closed tab loses the work. So a Buyer Home that said "4
  orders waiting" would be inventing a number, which is exactly what rule 9
  exists to stop.

  So it is a place to arrive rather than a scoreboard: the three tools, one line
  each saying what they do, and a way in. It says plainly that nothing is kept
  yet, because that is true and it is the next thing on the queue.

  **The one real number on it is the count that is running**, and it is there
  because a buyer orders against what a count found. It is read only: a buyer has
  no way into the counting screens and should not have.
*/

export default function BuyerHome({ profile, cycle, onOpen }) {
  const due = dueState(cycle?.due_on);

  const line = `${today()}. Two tools for ordering, and nothing else is asked of you.`;

  const headline = (
    <Card>
      <Kind>What you can do here</Kind>
      <div style={{ marginTop: 4 }}>
        <Need n="1" title="Client Ordering"
          sub="Build an order for one client, from their own catalog"
          onClick={() => onOpen('client_ordering')} />
        <Need n="2" title="Regional Product Movement"
          sub="Read handwritten movement pages and turn them into an import file"
          onClick={() => onOpen('inventory_movement')} />
      </div>
    </Card>
  );

  // **Rule 9's shape.** Saying nothing here would leave a person wondering where
  // yesterday's work went. Saying it plainly is the honest answer, and it is on
  // the queue under "Build the Ordering area".
  const kept = (
    <NeedsCard title="What OnHand keeps"
      rows={
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.65, padding: '10px 0 4px' }}>
          Nothing yet. Each of these two tools takes a file, works on it in this
          browser, and gives you a file back. <b>Close the tab and the work is
          gone</b>, and a colleague cannot pick it up. Keeping a record of what
          was ordered, and when, is the next thing being built.
        </div>
      } />
  );

  const counting = (
    <Card>
      <div style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--text-primary)' }}>The count that is running</div>
      {cycle ? (
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 10, lineHeight: 1.65 }}>
          <b style={{ color: 'var(--text-primary)' }}>{cycle.name}</b> is open.
          {due ? ' ' + due.line : ''}
          {' '}You order against what it finds, so it is here to be seen and not to be worked on.
        </div>
      ) : (
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 10, lineHeight: 1.65 }}>
          No count is open at the moment.
        </div>
      )}
    </Card>
  );

  return (
    <div>
      {/* **What changed in OnHand, above the greeting.** The owner's own
          instruction of 5 September 2026: the release note stops taking the
          whole screen and becomes a notice here with one button that clears
          it. It draws nothing at all when there is no unread note, which is
          almost always. Group H's drawing, and it changes this approved
          Group C screen because he asked for it. */}
      <ReleaseNote />
      <Greeting name={profile?.full_name} line={line} />
      <HomeFrame headline={headline} needs={kept} third={counting} />
    </div>
  );
}
